import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import { PASSWORD_VERY_STRONG, testRegex } from "../../config/regex";
import { resetPasswordWithToken } from "../../api";
import { Eye, EyeOff } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Reset link is invalid or has expired.");
    }
  }, [token]);

  const validateField = (field, value, otherValue) => {
    const v = String(value ?? "").trim();
    switch (field) {
      case "password":
        if (!v) return "Password is required";
        if (v.length < 8)
          return "Password must be 8 chars with 1 letter & 1 number & 1 special char";
        if (!testRegex(PASSWORD_VERY_STRONG, v))
          return "Must include uppercase, lowercase, number, and special character";
        return "";
      case "confirmPassword":
        if (!v) return "Confirmation is required";
        if (v !== (otherValue ?? password)) return "Passwords do not match";
        return "";
      default:
        return "";
    }
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    const passErr = validateField("password", val);
    
    // If confirm password already has a value, re-validate it too
    let confirmErr = fieldErrors.confirmPassword;
    if (confirmPassword) {
      confirmErr = validateField("confirmPassword", confirmPassword, val);
    }

    setFieldErrors(prev => ({ 
      ...prev, 
      password: passErr,
      confirmPassword: confirmErr
    }));
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setFieldErrors(prev => ({ ...prev, confirmPassword: validateField("confirmPassword", val) }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("Reset link is invalid or has expired.");
      return;
    }
    
    const passErr = validateField("password", password);
    const confirmErr = validateField("confirmPassword", confirmPassword);
    
    if (passErr || confirmErr) {
      setFieldErrors({ password: passErr, confirmPassword: confirmErr });
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await resetPasswordWithToken(token, password);
      if (res.success) {
        setSuccessMessage(
          "Your password has been reset successfully. You can now log in with your new password.",
        );
        setTimeout(() => navigate("/login"), 2500);
      } else {
        setError(res.message || "Failed to reset password");
      }
    } catch (_) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-12 lg:py-20">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10 border border-gray-100">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Reset Password
              </h2>
              <p className="text-gray-600">
                Enter a new password for your account.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-3 rounded-lg bg-success-50 text-success-700 text-sm">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <TextInput
                name="password"
                type={showPassword ? "text" : "password"}
                label="New password"
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter new password"
                error={fieldErrors.password}
                required
                icon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="text-gray-400 hover:text-primary-500 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
                iconPosition="right"
              />
              <TextInput
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm new password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                placeholder="Confirm new password"
                error={fieldErrors.confirmPassword}
                required
                icon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    className="text-gray-400 hover:text-primary-500 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                }
                iconPosition="right"
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full !py-3"
                disabled={loading || !token}
              >
                {loading ? "Updating..." : "Update password"}
              </Button>
            </form>

            <p className="mt-6 text-center text-gray-600">
              Remembered your password?{" "}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                Back to Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
